/**
 * Quem está logado, e o que acontece quando deixa de estar.
 *
 * A sessão começa na partida chamando `/auth/renovar`: o acesso mora em memória e morre
 * com a aba, então o que ressuscita a sessão de quem só recarregou a página é o cookie de
 * refresh. Enquanto essa primeira chamada não volta, a aplicação mostra *carregando* e não
 * a tela de entrada — piscar o login para quem já está logado é o tipo de detalhe que faz
 * uma tela parecer quebrada.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";

import { acompanharOSistema, aplicarTema } from "../tema";
import type { Tema } from "../tema";

import {
  ErroDaApi,
  aoExpirar,
  aoRenovar,
  api,
  guardarAcesso,
  guardarInatividade,
  minutosDeInatividade,
  renovar,
  ultimaRecusaDaSessao,
} from "./cliente";
import {
  ContextoDaSessao,
  EscolhaDeOrganizacao,
  PedidoDeSegundoFator,
} from "./contexto-sessao";
import type { CadastroDoSegundoFator } from "./contexto-sessao";
import type { Identidade, RespostaDeSessao } from "./tipos";

/** A última atividade vale para todas as abas: mexer numa mantém as outras vivas. */
const CHAVE_ATIVIDADE = "cockpit.ultima-atividade";
const EVENTOS_DE_ATIVIDADE = [
  "pointerdown",
  "keydown",
  "wheel",
  "touchstart",
] as const;

function lerAtividade(): number {
  try {
    return Number(window.localStorage.getItem(CHAVE_ATIVIDADE)) || 0;
  } catch {
    return 0;
  }
}

function gravarAtividade(instante: number): void {
  try {
    window.localStorage.setItem(CHAVE_ATIVIDADE, String(instante));
  } catch {
    // Navegador sem armazenamento: cada aba conta a sua, e ainda sai sozinha.
  }
}

export function ProvedorDeSessao({ children }: { children: ReactNode }) {
  const [quem, definirQuem] = useState<Identidade | null>(null);
  const [conferindo, definirConferindo] = useState(true);
  const [avisoDeSaida, definirAvisoDeSaida] = useState<string | null>(null);

  useEffect(() => {
    let vivo = true;
    // A renovação da partida não é um login: ela só descobre se o cookie ainda vale.
    void (async () => {
      const renovou = await renovar();
      if (!vivo) return;
      if (renovou) {
        try {
          definirQuem(await api<Identidade>("/auth/eu"));
        } catch {
          definirQuem(null);
        }
      } else {
        // O F5 de quem teve o acesso da organização vencido (A-09): a tela de entrada diz
        // até quando ela entrava, e não fica em branco.
        definirAvisoDeSaida(ultimaRecusaDaSessao());
      }
      definirConferindo(false);
    })();
    return () => {
      vivo = false;
    };
  }, []);

  useEffect(() => {
    // Quando a renovação falha no meio do uso, a aplicação não pode continuar desenhando
    // dados de uma sessão que acabou.
    aoExpirar(() => {
      definirAvisoDeSaida(
        ultimaRecusaDaSessao() ?? "Sua sessão terminou. Entre de novo.",
      );
      definirQuem(null);
    });
    // A renovação traz a identidade de novo: a validade e a marca d'água (A-09) chegam à
    // tela de quem está dentro sem recarregar. Só esses dois campos, e só quando mudaram: o
    // tema em voo não é desfeito por uma renovação que saiu antes da troca, e a identidade não
    // vira um objeto novo a cada 15 minutos — quem depende dela não recomeça à toa.
    aoRenovar((novo) => {
      const r = novo as Identidade;
      definirQuem((q) =>
        q &&
        q.usuario_id === r.usuario_id &&
        ((q.acesso_ate ?? null) !== (r.acesso_ate ?? null) ||
          q.marca_dagua !== r.marca_dagua)
          ? {
              ...q,
              acesso_ate: r.acesso_ate ?? null,
              marca_dagua: r.marca_dagua,
            }
          : q,
      );
    });
    return () => {
      aoExpirar(null);
      aoRenovar(null);
    };
  }, []);

  const entrar = useCallback(
    async (
      email: string,
      senha: string,
      organizacao?: string,
      codigo?: string,
    ) => {
      try {
        const sessao = await api<RespostaDeSessao>("/auth/sessao", {
          method: "POST",
          semRenovar: true,
          corpo: {
            email,
            senha,
            ...(organizacao ? { organizacao } : {}),
            ...(codigo ? { codigo } : {}),
          },
        });
        guardarAcesso(sessao.acesso);
        guardarInatividade(sessao.inatividade_minutos);
        gravarAtividade(Date.now());
        definirAvisoDeSaida(null);
        definirQuem(sessao.quem);
      } catch (erro) {
        if (erro instanceof ErroDaApi && erro.status === 409) {
          throw new EscolhaDeOrganizacao(
            await organizacoesDoConflito(email, senha),
          );
        }
        if (
          erro instanceof ErroDaApi &&
          (erro.motivo === "segundo_fator" ||
            erro.motivo === "cadastrar_segundo_fator")
        ) {
          const detalhe = erro.detalhe as {
            cadastro?: CadastroDoSegundoFator;
          };
          throw new PedidoDeSegundoFator(
            erro.message,
            detalhe.cadastro ?? null,
          );
        }
        throw erro;
      }
    },
    [],
  );

  const sair = useCallback(async () => {
    // O `catch` é de propósito: sair da tela não pode depender de a API responder. O
    // acesso morre aqui de qualquer jeito, e o cookie expira sozinho.
    await api("/auth/sair", { method: "POST", corpo: {} }).catch(
      () => undefined,
    );
    guardarAcesso(null);
    definirQuem(null);
  }, []);

  // Sair sozinho depois de um tempo sem uso (T-12, "o notebook aberto numa sala"). A
  // renovação automática mantém a sessão viva enquanto a aba está aberta — é por isso que
  // quem decide aqui é a atividade da pessoa, e não a da tela. Conta mouse, teclado, rolagem
  // e toque; a fila que se atualiza sozinha não conta.
  // `logado`, e não `quem`: trocar o tema ou receber a validade nova (A-09) não é atividade da
  // pessoa, e recomeçar o efeito marcaria uma.
  const logado = quem !== null;
  useEffect(() => {
    if (!logado) return;
    const limiteMs = minutosDeInatividade() * 60_000;
    if (limiteMs <= 0) return;
    let ultimaGravacao = 0;
    const marcar = () => {
      const agora = Date.now();
      // Uma gravação a cada 15 s basta: o limite é em minutos.
      if (agora - ultimaGravacao > 15_000) {
        ultimaGravacao = agora;
        gravarAtividade(agora);
      }
    };
    marcar();
    for (const evento of EVENTOS_DE_ATIVIDADE)
      window.addEventListener(evento, marcar, { passive: true });
    const relogio = window.setInterval(() => {
      const ultima = Math.max(lerAtividade(), ultimaGravacao);
      if (Date.now() - ultima >= limiteMs) {
        const minutos = Math.round(limiteMs / 60_000);
        definirAvisoDeSaida(
          `A sessão foi encerrada depois de ${minutos} minutos sem uso do cockpit. Entre de novo.`,
        );
        void sair();
      }
    }, 20_000);
    return () => {
      for (const evento of EVENTOS_DE_ATIVIDADE)
        window.removeEventListener(evento, marcar);
      window.clearInterval(relogio);
    };
  }, [logado, sair]);

  // O tema da pessoa (`C-19`, ADR-055). Chega com o login, a renovação da partida e o
  // `/auth/eu`; sem ninguém logado, a tela fica com o que o `public/tema.js` já pôs — a cópia
  // do último tema usado neste navegador. "Igual ao sistema" acompanha o sistema ao vivo.
  const tema = quem?.tema;
  useEffect(() => {
    if (!tema) return;
    aplicarTema(tema);
    if (tema !== "sistema") return;
    return acompanharOSistema(() => aplicarTema("sistema"));
  }, [tema]);

  // As gravações do tema vão **uma atrás da outra**: quem troca rápido (as setas do teclado
  // passam pelas três opções) não pode deixar na conta a penúltima, só porque ela chegou ao
  // servidor depois da última. E só a troca mais recente volta atrás se falhar — a que já foi
  // superada por outra não mexe na tela nem acusa erro.
  const ultimaTroca = useRef(0);
  const filaDoTema = useRef<Promise<unknown>>(Promise.resolve());
  // O último tema que o servidor **confirmou**: é para ele que a tela volta se a troca mais
  // recente falhar. Voltar para "o anterior" não bastava — com duas trocas seguidas que
  // falham, o anterior da segunda é a primeira, que nunca chegou à conta (revisão do
  // checkpoint).
  const confirmado = useRef<Tema | undefined>(undefined);
  const emVoo = useRef(0);
  const trocarTema = useCallback(
    async (novo: Tema) => {
      const anterior = quem?.tema;
      if (!quem || novo === anterior) return;
      // Sem gravação no ar, o tema na tela é o da conta.
      if (emVoo.current === 0) confirmado.current = anterior;
      emVoo.current += 1;
      const pedido = ++ultimaTroca.current;
      definirQuem((q) => (q ? { ...q, tema: novo } : q));
      const gravacao = filaDoTema.current
        .catch(() => undefined)
        .then(() =>
          api("/auth/eu/tema", { method: "PUT", corpo: { tema: novo } }),
        );
      filaDoTema.current = gravacao;
      try {
        await gravacao;
        confirmado.current = novo;
      } catch (erro) {
        if (pedido !== ultimaTroca.current) return;
        const volta = confirmado.current ?? anterior;
        if (volta) definirQuem((q) => (q ? { ...q, tema: volta } : q));
        throw erro;
      } finally {
        emVoo.current -= 1;
      }
    },
    [quem],
  );

  const valor = useMemo(
    () => ({ quem, conferindo, entrar, sair, avisoDeSaida, trocarTema }),
    [quem, conferindo, entrar, sair, avisoDeSaida, trocarTema],
  );
  return (
    <ContextoDaSessao.Provider value={valor}>
      {children}
    </ContextoDaSessao.Provider>
  );
}

/**
 * Relê o 409 para extrair a lista de organizações.
 *
 * Feio, e é o menor dos males: o `ErroDaApi` normaliza `detail` para texto, porque é isso
 * que 99% das telas precisam. Refazer a chamada aqui mantém esse contrato simples em vez
 * de fazer todo erro carregar um `unknown` que ninguém mais usa.
 */
async function organizacoesDoConflito(email: string, senha: string) {
  const resposta = await fetch("/auth/sessao", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email, senha }),
  });
  const corpo = (await resposta.json().catch(() => ({}))) as {
    detail?: { organizacoes?: { id: string; nome: string }[] };
  };
  return corpo.detail?.organizacoes ?? [];
}
